import React, { useRef, useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, MapPin, ChevronRight, ExternalLink } from 'lucide-react';
import { api } from '../api/client';
import { FacilityRoom } from '../hooks/useFacilityRooms';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RoomUsageEntry {
  entry_id: number;
  wall_name: string;
  wall_label: string | null;
  last_painted_date: string | null;
  room_id: number;
  room_name: string;
  room_number: string | null;
  floor_name: string;
  floor_index: number;
  x: number;
  y: number;
  w: number;
  h: number;
  room_type: string;
  linked_area_id: number | null;
  linked_location_id: number | null;
  area_name: string | null;
}

interface GroupedRoom {
  room_id: number;
  room_name: string;
  room_number: string | null;
  floor_name: string;
  floor_index: number;
  x: number;
  y: number;
  w: number;
  h: number;
  room_type: string;
  linked_location_id: number | null;
  walls: { entry_id: number; wall_name: string; wall_label: string | null; last_painted_date: string | null }[];
}

interface PaintSupplyInfo {
  id: number;
  name: string;
  brand?: string | null;
  color?: string | null;
  finish?: string | null;
  image_url?: string | null;
}

interface Props {
  supply: PaintSupplyInfo;
  onClose: () => void;
}

// ─── Canvas colors (mirror FacilityMap.tsx) ───────────────────────────────────

const ROOM_COLORS: Record<string, { fill: string; stroke: string; text: string }> = {
  resident:   { fill: '#dbeafe', stroke: '#93c5fd', text: '#1e40af' },
  medical:    { fill: '#dcfce7', stroke: '#86efac', text: '#166534' },
  common:     { fill: '#fef9c3', stroke: '#fde047', text: '#713f12' },
  service:    { fill: '#fce7f3', stroke: '#f9a8d4', text: '#9d174d' },
  mechanical: { fill: '#fee2e2', stroke: '#fca5a5', text: '#991b1b' },
  admin:      { fill: '#ede9fe', stroke: '#c4b5fd', text: '#5b21b6' },
  hallway:    { fill: '#f3f4f6', stroke: '#d1d5db', text: '#374151' },
  other:      { fill: '#fff7ed', stroke: '#fdba74', text: '#92400e' },
};

function worldToCanvas(wx: number, wy: number, ox: number, oy: number, scale: number) {
  return { cx: wx * scale + ox, cy: wy * scale + oy };
}

// ─── MiniMapCanvas ────────────────────────────────────────────────────────────

function MiniMapCanvas({
  rooms,
  highlightRoomId,
}: {
  rooms: FacilityRoom[];
  highlightRoomId: number | null;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || rooms.length === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    // Compute bounds
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const r of rooms) {
      if (r.points && r.points.length > 2) {
        for (const pt of r.points) {
          minX = Math.min(minX, pt.x); minY = Math.min(minY, pt.y);
          maxX = Math.max(maxX, pt.x); maxY = Math.max(maxY, pt.y);
        }
      } else {
        const x = Number(r.x), y = Number(r.y);
        minX = Math.min(minX, x); minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + Number(r.w)); maxY = Math.max(maxY, y + Number(r.h));
      }
    }

    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    const rawScale = Math.min((W - 48) / rangeX, (H - 48) / rangeY);
    const scale = Math.min(Math.max(rawScale, 0.2), 4.0);
    const ox = (W - rangeX * scale) / 2 - minX * scale;
    const oy = (H - rangeY * scale) / 2 - minY * scale;

    // Clear
    ctx.clearRect(0, 0, W, H);

    // Draw all rooms
    for (const r of rooms) {
      const isHighlighted = r.id === highlightRoomId;
      const colors = ROOM_COLORS[r.type] ?? ROOM_COLORS.other;

      if (r.points && r.points.length > 2) {
        ctx.beginPath();
        r.points.forEach((pt, idx) => {
          const { cx: px, cy: py } = worldToCanvas(pt.x, pt.y, ox, oy, scale);
          if (idx === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        });
        ctx.closePath();
        ctx.fillStyle = isHighlighted ? '#bfdbfe' : colors.fill;
        ctx.fill();
        ctx.strokeStyle = isHighlighted ? '#2563eb' : colors.stroke;
        ctx.lineWidth = isHighlighted ? 2.5 : 1.5;
        ctx.stroke();
      } else {
        const { cx: rx, cy: ry } = worldToCanvas(Number(r.x), Number(r.y), ox, oy, scale);
        const rw = Number(r.w) * scale;
        const rh = Number(r.h) * scale;

        if (r.type === 'hallway') {
          ctx.fillStyle = 'rgba(200,200,195,0.4)';
          ctx.beginPath();
          ctx.roundRect(rx, ry, rw, rh, 2);
          ctx.fill();
          ctx.strokeStyle = 'rgba(150,150,145,0.5)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.roundRect(rx, ry, rw, rh, 2);
          ctx.stroke();
          continue;
        }

        ctx.fillStyle = isHighlighted ? '#bfdbfe' : colors.fill;
        ctx.beginPath();
        ctx.roundRect(rx, ry, rw, rh, 2);
        ctx.fill();

        ctx.strokeStyle = isHighlighted ? '#2563eb' : colors.stroke;
        ctx.lineWidth = isHighlighted ? 2.5 : 1.5;
        ctx.beginPath();
        ctx.roundRect(rx, ry, rw, rh, 2);
        ctx.stroke();

        // Highlight glow
        if (isHighlighted && rw > 20 && rh > 15) {
          ctx.save();
          ctx.shadowColor = '#3b82f6';
          ctx.shadowBlur = 10;
          ctx.strokeStyle = '#2563eb';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.roundRect(rx - 1, ry - 1, rw + 2, rh + 2, 3);
          ctx.stroke();
          ctx.restore();
        }
      }
    }
  }, [rooms, highlightRoomId]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
}

// ─── PaintUsedInPanel ─────────────────────────────────────────────────────────

export default function PaintUsedInPanel({ supply, onClose }: Props) {
  const [entries, setEntries] = useState<RoomUsageEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [floorRooms, setFloorRooms] = useState<FacilityRoom[]>([]);
  const [loadingFloor, setLoadingFloor] = useState(false);

  // Load usage entries
  useEffect(() => {
    setLoading(true);
    api.get<RoomUsageEntry[]>(`/facility-rooms/paint-supply/${supply.id}/rooms`)
      .then(res => {
        const data = res.data ?? [];
        setEntries(data);
        if (data.length > 0) setSelectedRoomId(data[0].room_id);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [supply.id]);

  // Group by room
  const groupedRooms = useMemo<GroupedRoom[]>(() => {
    const map = new Map<number, GroupedRoom>();
    for (const e of entries) {
      if (!map.has(e.room_id)) {
        map.set(e.room_id, {
          room_id: e.room_id,
          room_name: e.room_name,
          room_number: e.room_number,
          floor_name: e.floor_name,
          floor_index: e.floor_index,
          x: e.x, y: e.y, w: e.w, h: e.h,
          room_type: e.room_type,
          linked_location_id: e.linked_location_id,
          walls: [],
        });
      }
      map.get(e.room_id)!.walls.push({
        entry_id: e.entry_id,
        wall_name: e.wall_name,
        wall_label: e.wall_label,
        last_painted_date: e.last_painted_date,
      });
    }
    return Array.from(map.values()).sort((a, b) =>
      a.floor_index !== b.floor_index ? a.floor_index - b.floor_index : a.room_name.localeCompare(b.room_name)
    );
  }, [entries]);

  // Group by floor for display
  const byFloor = useMemo(() => {
    const map = new Map<string, GroupedRoom[]>();
    for (const r of groupedRooms) {
      const arr = map.get(r.floor_name) ?? [];
      arr.push(r);
      map.set(r.floor_name, arr);
    }
    return map;
  }, [groupedRooms]);

  const selectedRoom = groupedRooms.find(r => r.room_id === selectedRoomId) ?? null;

  // Load all rooms on selected room's floor for mini map
  useEffect(() => {
    if (!selectedRoom) { setFloorRooms([]); return; }
    setLoadingFloor(true);
    api.get<FacilityRoom[]>(`/facility-rooms`, { params: { floor_index: selectedRoom.floor_index } })
      .then(res => setFloorRooms(res.data ?? []))
      .catch(console.error)
      .finally(() => setLoadingFloor(false));
  }, [selectedRoom?.floor_index]);

  function fmtDate(val: string | null | undefined) {
    if (!val) return null;
    const s = String(val).slice(0, 10);
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function navigateToRoom(room: GroupedRoom) {
    if (!room.linked_location_id) return;
    window.location.href = `/inventory?tab=facility-map&room=${room.linked_location_id}`;
  }

  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex' }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'absolute', inset: '40px', borderRadius: 12, overflow: 'hidden',
          background: 'rgba(20,30,50,0.97)', border: '1px solid rgba(255,255,255,0.10)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0, background: 'rgba(255,255,255,0.03)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <MapPin size={18} style={{ color: '#60a5fa' }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                Used In — {supply.name}
              </div>
              {(supply.brand || supply.color) && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
                  {[supply.brand, supply.color, supply.finish].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {loading ? '…' : `${groupedRooms.length} room${groupedRooms.length !== 1 ? 's' : ''}`}
            </span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* Left — room list */}
          <div style={{
            width: 340, flexShrink: 0,
            borderRight: '1px solid rgba(255,255,255,0.08)',
            overflowY: 'auto', display: 'flex', flexDirection: 'column',
          }}>
            {loading ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Loading…
              </div>
            ) : groupedRooms.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Not used in any rooms yet.
              </div>
            ) : (
              Array.from(byFloor.entries()).map(([floorName, rooms]) => (
                <div key={floorName}>
                  {/* Floor header */}
                  <div style={{
                    padding: '8px 16px', fontSize: '0.68rem', fontWeight: 700,
                    color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.08em',
                    background: 'rgba(96,165,250,0.06)', borderBottom: '1px solid rgba(255,255,255,0.05)',
                    position: 'sticky', top: 0,
                  }}>
                    {floorName}
                  </div>

                  {rooms.map(room => {
                    const isSelected = room.room_id === selectedRoomId;
                    return (
                      <div
                        key={room.room_id}
                        onClick={() => setSelectedRoomId(room.room_id)}
                        style={{
                          padding: '10px 16px', cursor: 'pointer',
                          background: isSelected ? 'rgba(59,130,246,0.12)' : 'transparent',
                          borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                          transition: 'background 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 600, fontSize: '0.83rem', color: isSelected ? '#93c5fd' : 'var(--text-primary)' }}>
                              {room.room_name}
                            </span>
                            {room.room_number && (
                              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', opacity: 0.7 }}>
                                #{room.room_number}
                              </span>
                            )}
                          </div>
                          <ChevronRight size={13} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
                        </div>
                        {/* Wall pills */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {room.walls.map(w => (
                            <span
                              key={w.entry_id}
                              style={{
                                padding: '2px 7px', borderRadius: 10, fontSize: '0.68rem',
                                background: isSelected ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.06)',
                                color: isSelected ? '#93c5fd' : 'var(--text-secondary)',
                                border: `1px solid ${isSelected ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.1)'}`,
                                fontWeight: 500,
                              }}
                            >
                              {w.wall_label ?? w.wall_name}
                              {w.last_painted_date && (
                                <span style={{ opacity: 0.6, marginLeft: 3 }}>
                                  · {fmtDate(w.last_painted_date)}
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Right — mini map + info */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {selectedRoom ? (
              <>
                {/* Mini map */}
                <div style={{ flex: 1, position: 'relative', background: 'rgba(15,22,40,0.6)', overflow: 'hidden' }}>
                  {loadingFloor ? (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.83rem' }}>
                      Loading map…
                    </div>
                  ) : floorRooms.length > 0 ? (
                    <MiniMapCanvas rooms={floorRooms} highlightRoomId={selectedRoomId} />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.83rem' }}>
                      No floor map available
                    </div>
                  )}

                  {/* Floor badge */}
                  <div style={{
                    position: 'absolute', top: 12, left: 12,
                    padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                    background: 'rgba(20,30,50,0.85)', color: '#60a5fa',
                    border: '1px solid rgba(96,165,250,0.25)',
                  }}>
                    {selectedRoom.floor_name}
                  </div>
                </div>

                {/* Room info footer */}
                <div style={{
                  padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(255,255,255,0.02)', flexShrink: 0,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)', marginBottom: 4 }}>
                        {selectedRoom.room_name}
                        {selectedRoom.room_number && (
                          <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            Room #{selectedRoom.room_number}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {selectedRoom.walls.map(w => (
                          <span
                            key={w.entry_id}
                            style={{
                              padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem',
                              background: 'rgba(59,130,246,0.12)', color: '#93c5fd',
                              border: '1px solid rgba(59,130,246,0.2)', fontWeight: 500,
                            }}
                          >
                            {w.wall_label ?? w.wall_name}
                            {w.last_painted_date && ` · ${fmtDate(w.last_painted_date)}`}
                          </span>
                        ))}
                      </div>
                    </div>

                    {selectedRoom.linked_location_id && (
                      <button
                        onClick={() => navigateToRoom(selectedRoom)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '7px 14px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600,
                          background: 'rgba(59,130,246,0.15)', color: '#60a5fa',
                          border: '1px solid rgba(59,130,246,0.3)', cursor: 'pointer',
                          flexShrink: 0, whiteSpace: 'nowrap',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(59,130,246,0.25)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'rgba(59,130,246,0.15)')}
                      >
                        <ExternalLink size={13} />
                        View full room detail
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Select a room to view on map
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
